import { generateId } from '../utils/uuid';

// Notification Service - Browser push notifications for tasks and events

// Check if notifications are supported
export const isNotificationSupported = (): boolean => {
    return typeof Notification !== 'undefined';
};

// Get current permission status
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
    if (!isNotificationSupported()) return 'unsupported';
    try {
        return Notification.permission;
    } catch (e) {
        return 'unsupported';
    }
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
    if (!isNotificationSupported()) {
        console.warn('[Notifications] Not supported in this browser');
        return 'unsupported';
    }

    try {
        const permission = await Notification.requestPermission();
        console.log('[Notifications] Permission:', permission);
        return permission;
    } catch (error) {
        console.error('[Notifications] Error requesting permission:', error);
        // Some browsers (like mobile Safari in some modes) might throw if called incorrectly
        return 'unsupported';
    }
};

// Notification options
interface NotificationOptions {
    title: string;
    body: string;
    icon?: string;
    tag?: string; // Unique ID to prevent duplicates
    data?: {
        type: 'task' | 'event' | 'habit' | 'goal';
        id: string;
        url?: string;
    };
    requireInteraction?: boolean;
}

// Show a notification
export const showNotification = (options: NotificationOptions): Notification | null => {
    if (!isNotificationSupported()) {
        console.warn('[Notifications] Not supported');
        return null;
    }

    if (Notification.permission !== 'granted') {
        console.warn('[Notifications] Permission not granted');
        return null;
    }

    try {
        const notification = new Notification(options.title, {
            body: options.body,
            icon: options.icon || '/favicon.ico',
            tag: options.tag,
            data: options.data,
            requireInteraction: options.requireInteraction || false,
        });

        // Handle notification click
        notification.onclick = () => {
            window.focus();
            if (options.data?.url) {
                window.location.href = options.data.url;
            }
            notification.close();
        };

        return notification;
    } catch (error) {
        console.error('[Notifications] Error showing notification:', error);
        return null;
    }
};

// Storage key for pending reminders
const REMINDERS_KEY = 'way2will_pending_reminders';

export interface PendingReminder {
    id: string;
    type: 'task' | 'event' | 'habit';
    itemId: string;
    title: string;
    description?: string;
    triggerTime: string; // ISO string
    notified: boolean;
}

// Get pending reminders
export const getPendingReminders = (): PendingReminder[] => {
    const stored = localStorage.getItem(REMINDERS_KEY);
    return stored ? JSON.parse(stored) : [];
};

// Save pending reminders
export const savePendingReminders = (reminders: PendingReminder[]): void => {
    localStorage.setItem(REMINDERS_KEY, JSON.stringify(reminders));
};

// Schedule a reminder
export const scheduleReminder = (reminder: Omit<PendingReminder, 'id' | 'notified'>): void => {
    const reminders = getPendingReminders();

    // Check if already scheduled
    const exists = reminders.some(
        r => r.itemId === reminder.itemId && r.triggerTime === reminder.triggerTime
    );

    if (exists) return;

    const newReminder: PendingReminder = {
        id: generateId(),
        ...reminder,
        notified: false,
    };

    reminders.push(newReminder);
    savePendingReminders(reminders);
    console.log('[Notifications] Reminder scheduled:', newReminder);
};

// Cancel a reminder
export const cancelReminder = (itemId: string): void => {
    const reminders = getPendingReminders();
    const filtered = reminders.filter(r => r.itemId !== itemId);
    savePendingReminders(filtered);
};

// Check and trigger due reminders
export const checkDueReminders = (): void => {
    if (!isNotificationSupported()) return;
    if (Notification.permission !== 'granted') return;

    const reminders = getPendingReminders();
    const now = new Date();
    let hasChanges = false;

    reminders.forEach(reminder => {
        if (reminder.notified) return;

        const triggerTime = new Date(reminder.triggerTime);
        if (triggerTime <= now) {
            // Trigger notification
            showNotification({
                title: reminder.title,
                body: reminder.description || getDefaultBody(reminder.type),
                tag: reminder.id,
                data: {
                    type: reminder.type,
                    id: reminder.itemId,
                    url: getUrlForType(reminder.type, reminder.itemId),
                },
                requireInteraction: true,
            });

            reminder.notified = true;
            hasChanges = true;
        }
    });

    if (hasChanges) {
        // Clean up old notified reminders (older than 1 hour)
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const cleaned = reminders.filter(r => {
            if (!r.notified) return true;
            return new Date(r.triggerTime) > oneHourAgo;
        });
        savePendingReminders(cleaned);
    }
};

// Get default notification body
const getDefaultBody = (type: 'task' | 'event' | 'habit'): string => {
    switch (type) {
        case 'task':
            return 'This task is due soon!';
        case 'event':
            return 'This event is starting soon!';
        case 'habit':
            return "Don't forget to complete your habit today!";
        default:
            return 'Reminder';
    }
};

// Get URL for notification click
const getUrlForType = (type: 'task' | 'event' | 'habit', id: string): string => {
    switch (type) {
        case 'task':
            return `/app/task/${id}`;
        case 'event':
            return `/app/event/${id}`;
        case 'habit':
            return '/app/habits';
        default:
            return '/app';
    }
};

// Calculate reminder time based on offset
export const calculateReminderTime = (
    dueDate: string,
    dueTime: string | undefined,
    offsetMinutes: number
): Date => {
    let targetTime: Date;

    if (dueTime) {
        // Combine date and time
        targetTime = new Date(`${dueDate}T${dueTime}`);
    } else {
        // Use start of day (9 AM default for all-day items)
        targetTime = new Date(`${dueDate}T09:00:00`);
    }

    // Subtract offset
    targetTime.setMinutes(targetTime.getMinutes() - offsetMinutes);
    return targetTime;
};

// Reminder offset options
export const REMINDER_OPTIONS = [
    { value: 0, label: 'At time' },
    { value: 5, label: '5 minutes before' },
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 1440, label: '1 day before' },
];

// Start the reminder check interval
let reminderInterval: number | null = null;

export const startReminderChecker = (): void => {
    if (reminderInterval) return;

    // Check every minute
    reminderInterval = window.setInterval(checkDueReminders, 60000);

    // Also check immediately
    checkDueReminders();

    console.log('[Notifications] Reminder checker started');
};

export const stopReminderChecker = (): void => {
    if (reminderInterval) {
        clearInterval(reminderInterval);
        reminderInterval = null;
        console.log('[Notifications] Reminder checker stopped');
    }
};
